/* ═══════════════════════════════════════════════════════════════════════
   VMFS SVG RENDERER — SNACK & BEVERAGE
   Machine-specific renderer for the snack/beverage configurator family.

   Anatomy:
   - Cabinet body (Standard with thick black frame OR Smart sleek white
     OR Advertising full-screen LED variant)
   - Glass product window with realistic snacks/candy/bottles/cans/water
   - Coil / push / conveyor / elevator delivery mechanisms
   - Right-hand control panel (Classic keypad / Medium touch / Smart big touch)
   - Payment + delivery door + base

   Calls window.VMFS.shared.* for: defs, top box, canopy, camera,
   side LEDs, IP65, connectivity, feet, logos, payment row, coin return,
   ID scanner, delivery door.

   Registers as: window.VMFS.registerRenderer('snack-beverage', { build, mapState })
   ═══════════════════════════════════════════════════════════════════════ */
(function(){
'use strict';

const S = window.VMFS && window.VMFS.shared;
if(!S){
  console.error("[VMFS] vmfs-svg-shared.js must load before this renderer.");
  return;
}

const VB_W = 520, VB_H = 820;

const SIZES = {
  standard: { mW:290, mH:540, cols:5, rows:5, label:'Standard', sub:'42″ × 72″ × 36″' },
  large:    { mW:370, mH:575, cols:7, rows:6, label:'Large',    sub:'54″ × 75″ × 38″' }
};

const TEMP_THEME = {
  ambient: { interior:'#1F2937', overlay:'transparent',              overlayOp:0,    frost:false, icon:'' },
  cooled:  { interior:'#1E3A5F', overlay:'rgba(147,197,253,0.28)',   overlayOp:1,    frost:false, icon:'❄' },
  frozen:  { interior:'#0F2847', overlay:'rgba(219,234,254,0.45)',   overlayOp:1,    frost:true,  icon:'❄' }
};

const PRODUCT_PALETTE = {
  snack: [
    {body:'#F97316', label:'#FFFFFF', name:'#FFEAD5'},
    {body:'#DC2626', label:'#FEF3C7', name:'#FFFFFF'},
    {body:'#FACC15', label:'#1F2937', name:'#78350F'},
    {body:'#16A34A', label:'#FFFFFF', name:'#FEF3C7'},
    {body:'#7C3AED', label:'#FEF3C7', name:'#FFFFFF'},
    {body:'#EA580C', label:'#FFFFFF', name:'#FED7AA'},
    {body:'#0891B2', label:'#FFFFFF', name:'#E0F2FE'}
  ],
  candy: [
    {body:'#92400E', label:'#FEF3C7', name:'#FFFFFF'},
    {body:'#DC2626', label:'#FFFFFF', name:'#FEE2E2'},
    {body:'#1E40AF', label:'#FACC15', name:'#FFFFFF'},
    {body:'#EC4899', label:'#FFFFFF', name:'#FCE7F3'},
    {body:'#065F46', label:'#FDE68A', name:'#FFFFFF'},
    {body:'#7C2D12', label:'#FED7AA', name:'#FFFFFF'},
    {body:'#4C1D95', label:'#FFFFFF', name:'#F3E8FF'}
  ],
  bottle: [
    {body:'#1F2937', label:'#DC2626', name:'#FFFFFF'},
    {body:'#1E40AF', label:'#FFFFFF', name:'#DBEAFE'},
    {body:'#15803D', label:'#FFFFFF', name:'#DCFCE7'},
    {body:'#F97316', label:'#FFFFFF', name:'#FFEDD5'},
    {body:'#7C3AED', label:'#FACC15', name:'#F3E8FF'},
    {body:'#DC2626', label:'#FFFFFF', name:'#FEE2E2'},
    {body:'#0F766E', label:'#FFFFFF', name:'#CCFBF1'}
  ],
  can: [
    {body:'#DC2626', label:'#FFFFFF', name:'#FEE2E2'},
    {body:'#1E40AF', label:'#FACC15', name:'#DBEAFE'},
    {body:'#15803D', label:'#FFFFFF', name:'#DCFCE7'},
    {body:'#F59E0B', label:'#1F2937', name:'#FEF3C7'},
    {body:'#111827', label:'#10B981', name:'#064E3B'},
    {body:'#7C2D12', label:'#FDE68A', name:'#FEF3C7'},
    {body:'#831843', label:'#FDE68A', name:'#FCE7F3'}
  ],
  water: [
    {body:'#E0F2FE', label:'#0284C7', name:'#0369A1'},
    {body:'#F0F9FF', label:'#0369A1', name:'#075985'},
    {body:'#DBEAFE', label:'#1D4ED8', name:'#1E3A8A'},
    {body:'#DCFCE7', label:'#15803D', name:'#166534'},
    {body:'#FEF3C7', label:'#B45309', name:'#92400E'},
    {body:'#F5F5F4', label:'#57534E', name:'#1C1917'},
    {body:'#E0F2FE', label:'#0284C7', name:'#0369A1'}
  ]
};

/* ═══════════════════════════════════════════════════════════════════════
   MAIN ENTRY
   ═══════════════════════════════════════════════════════════════════════ */
function buildMachineSVG(s){
  const sz = SIZES[s.size] || SIZES.standard;
  const mW = sz.mW, mH = sz.mH;
  const hasTopBox = s.topbox && s.topbox !== 'none';
  const topBoxH = hasTopBox ? 46 : 0;
  const mY = 90 + topBoxH + (s.env === 'outdoor' ? 14 : 0);
  const mX = (VB_W - mW) / 2;

  let svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${VB_W} ${VB_H}"
    width="100%" preserveAspectRatio="xMidYMax meet" style="display:block;max-width:100%;max-height:620px">`;

  svg += S.buildDefs();

  /* Soft ground shadow */
  svg += `<ellipse cx="${VB_W/2}" cy="${mY+mH+24}" rx="${mW*0.58}" ry="9"
    fill="#000" fill-opacity="0.13"/>`;

  if(hasTopBox) svg += S.buildTopBox(s.topbox, mX, mY-topBoxH-6, mW, topBoxH);
  if(s.env === 'outdoor') svg += S.buildCanopy(mX, mY, mW);

  if(s.camera){
    const camY = s.env === 'outdoor' ? mY-30 : mY+10;
    svg += S.buildCamera(mX + mW/2, camY);
  }

  if(s.system === 'advertising'){
    svg += buildAdvertisingBody(s, sz, mX, mY, mW, mH);
  } else {
    svg += buildCabinetBody(s, sz, mX, mY, mW, mH);
  }

  if(s.env === 'outdoor' || s.led){
    svg += S.buildSideLEDs(mX, mY, mW, mH);
    if(s.led){
      svg += `<ellipse cx="${VB_W/2}" cy="${mY+mH+20}" rx="${mW*0.48}" ry="7"
        fill="#3B82F6" fill-opacity="0.16" filter="url(#glow)"/>`;
    }
  }

  svg += S.buildFeet(mX, mY+mH, mW, s.system === 'advertising');
  if(s.env === 'outdoor') svg += S.buildIP65Badge(mX + mW - 42, mY+mH - 22);
  svg += S.buildConnectivityBadges(s, mX + 8, mY + mH - 22);

  /* Size label */
  const labelY = mY + mH + 48;
  svg += `<text x="${VB_W/2}" y="${labelY}" text-anchor="middle"
    font-family="-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif"
    font-size="13" font-weight="800" fill="#1F2937" letter-spacing="1.5">${sz.label.toUpperCase()}</text>`;
  svg += `<text x="${VB_W/2}" y="${labelY+16}" text-anchor="middle"
    font-family="-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif"
    font-size="10" font-weight="600" fill="#9CA3AF" letter-spacing="2">${sz.sub}</text>`;

  svg += `</svg>`;
  return svg;
}

/* ═══════════════════════════════════════════════════════════════════════
   CABINET BODY (Standard + Smart) — window on left, controls on right
   ═══════════════════════════════════════════════════════════════════════ */
function buildCabinetBody(s, sz, mX, mY, mW, mH){
  const bodyCol = S.getBodyColor(s);
  const isFullWrap = s.wrap === 'full' && s.wrapColor;
  const hasBlackFrame = s.system === 'standard' && !isFullWrap;
  const frameT = hasBlackFrame ? 14 : 0;

  const innerX = mX + frameT;
  const innerY = mY + frameT;
  const innerW = mW - frameT*2;
  const innerH = mH - frameT*2;

  const winX   = innerX + 8;
  const winW   = innerW * 0.60;
  const ctrlX  = innerX + winW + 14;
  const ctrlW  = innerW - winW - 22;
  const contentY = innerY + 8;
  const contentH = innerH - 54;
  const doorY  = innerY + innerH - 44;

  let out = `<g id="cabinet-body" filter="url(#bodyShadow)">`;

  if(hasBlackFrame){
    out += `<rect x="${mX}" y="${mY}" width="${mW}" height="${mH}" rx="6" fill="url(#blackFrame)"/>`;
    out += `<rect x="${mX+1}" y="${mY+1}" width="${mW-2}" height="2" fill="rgba(255,255,255,0.06)"/>`;
    out += `<rect x="${mX+1}" y="${mY+mH-3}" width="${mW-2}" height="2" fill="rgba(0,0,0,0.4)"/>`;
    out += `<rect x="${innerX}" y="${innerY}" width="${innerW}" height="${innerH}" rx="3" fill="${bodyCol}"
      stroke="#E5E7EB" stroke-width="0.75"/>`;
    for(let i=0;i<4;i++){
      const vY = mY + mH*0.25 + i*12;
      out += `<rect x="${mX+3}" y="${vY}" width="${frameT-6}" height="1.5" fill="rgba(255,255,255,0.08)"/>`;
      out += `<rect x="${mX+mW-frameT+3}" y="${vY}" width="${frameT-6}" height="1.5" fill="rgba(255,255,255,0.08)"/>`;
    }
  } else {
    out += `<rect x="${mX}" y="${mY}" width="${mW}" height="${mH}" rx="6"
      fill="${isFullWrap ? bodyCol : 'url(#bodyShell)'}"
      stroke="${S.getBodyEdgeColor(s)}" stroke-width="1.2"/>`;
    out += `<rect x="${mX+1.5}" y="${mY+1.5}" width="${mW-3}" height="${mH-3}" rx="4.5"
      fill="none" stroke="rgba(0,0,0,0.04)" stroke-width="1"/>`;
    out += `<rect x="${mX+2}" y="${mY+12}" width="3" height="${mH-24}" rx="1.5" fill="rgba(255,255,255,0.6)"/>`;
  }

  if(s.wrap === 'logo'){
    const logoColor = isFullWrap && bodyCol !== '#FFFFFF' ? '#FFFFFF' : (hasBlackFrame ? '#FFFFFF' : '#1F2937');
    if(hasBlackFrame){
      out += `<text x="${mX+mW/2}" y="${mY+frameT*0.7}" text-anchor="middle"
        font-family="-apple-system,sans-serif" font-size="${Math.min(frameT*0.7, 10)}" font-weight="900"
        letter-spacing="2" fill="${logoColor}">VMFS USA</text>`;
    } else {
      out += S.buildVmfsLogo(mX+12, mY+mH-70, 14, logoColor);
    }
  }

  out += buildProductWindow(s, sz, winX, contentY, winW, contentH);
  out += buildControlPanel(s, ctrlX, contentY, ctrlW, contentH);
  out += S.buildDeliveryDoor(winX, doorY, winW, 36, s.system === 'smart');

  /* Bottom air vent under controls */
  out += `<g>`;
  for(let i=0;i<6;i++){
    out += `<rect x="${ctrlX+6+i*10}" y="${doorY+8}" width="6" height="22" rx="1"
      fill="rgba(0,0,0,0.09)"/>`;
  }
  out += `</g>`;

  out += `</g>`;
  return out;
}

/* ═══════════════════════════════════════════════════════════════════════
   PRODUCT WINDOW
   ═══════════════════════════════════════════════════════════════════════ */
function buildProductWindow(s, sz, x, y, w, h){
  const cols = sz.cols;
  const rows = sz.rows;
  const temp = TEMP_THEME[s.temp] || TEMP_THEME.ambient;

  const padL = 4, padR = 4, padT = 6, padB = 12;
  const slotsX = x + padL;
  const slotsY = y + padT;
  const slotsW = w - padL - padR;
  const slotsH = h - padT - padB;
  const slotW = slotsW / cols;
  const slotH = slotsH / rows;

  let out = `<g id="product-window">`;
  out += `<rect x="${x-2}" y="${y-2}" width="${w+4}" height="${h+4}" rx="3" fill="#1F2937"/>`;
  out += `<rect x="${x-1}" y="${y-1}" width="${w+2}" height="${h+2}" rx="2" fill="#111827"/>`;
  out += `<rect x="${x}" y="${y}" width="${w}" height="${h}" fill="${temp.interior}"/>`;

  for(let r=0;r<rows;r++){
    const rowType = getRowType(r, rows);
    const rowY = slotsY + r * slotH;
    const rowH = slotH;

    for(let c=0;c<cols;c++){
      const palette = PRODUCT_PALETTE[rowType][c % PRODUCT_PALETTE[rowType].length];
      const pX = slotsX + c * slotW;
      out += drawProduct(rowType, s.delivery, pX, rowY, slotW, rowH, palette, r, c);
    }

    if(r < rows - 1){
      const shelfY = rowY + rowH - 3;
      if(s.delivery === 'conveyor'){
        out += `<rect x="${x}" y="${shelfY-2}" width="${w}" height="8"
          fill="#1E293B" stroke="#0F172A" stroke-width="0.5"/>`;
        out += `<rect x="${x}" y="${shelfY}" width="${w}" height="4" fill="#475569"/>`;
        const chevCount = Math.floor(w/10);
        for(let i=0;i<chevCount;i++){
          const chx = x + 3 + i*10;
          out += `<polygon points="${chx},${shelfY+0.5} ${chx+6},${shelfY+0.5} ${chx+4},${shelfY+3.5} ${chx-2},${shelfY+3.5}"
            fill="#CBD5E1"/>`;
        }
        out += `<text x="${x+4}" y="${shelfY+11}" font-family="-apple-system,sans-serif"
          font-size="3" font-weight="700" letter-spacing="1" fill="#64748B">CONVEYOR →</text>`;
      } else {
        out += `<rect x="${x}" y="${shelfY}" width="${w}" height="3" fill="url(#shelfGrad)"/>`;
        out += `<rect x="${x}" y="${shelfY+3}" width="${w}" height="1" fill="#1F2937" opacity="0.5"/>`;
      }
    }
  }

  /* Elevator rail on right side */
  if(s.delivery === 'elevator'){
    const elvX = x + w - 10;
    out += `<g id="elevator">
      <rect x="${elvX}" y="${y+4}" width="6" height="${h-8}" rx="1" fill="#1E293B"/>
      <rect x="${elvX+1}" y="${y+4}" width="4" height="${h-8}" rx="0.5"
        fill="none" stroke="#60A5FA" stroke-width="0.5" stroke-dasharray="3,2"/>
      <rect x="${elvX-2}" y="${y+h*0.55}" width="10" height="4" rx="1" fill="#60A5FA"/>
      <rect x="${elvX-2}" y="${y+h*0.55+4}" width="10" height="1" fill="#1E40AF"/>
      <polygon points="${elvX+3},${y+10} ${elvX+5.5},${y+13} ${elvX+0.5},${y+13}" fill="#60A5FA"/>
      <polygon points="${elvX+3},${y+h-10} ${elvX+5.5},${y+h-13} ${elvX+0.5},${y+h-13}" fill="#60A5FA"/>
    </g>`;
  }

  /* Temperature overlay */
  if(temp.overlay !== 'transparent'){
    out += `<rect x="${x}" y="${y}" width="${w}" height="${h}" fill="${temp.overlay}"/>`;
  }

  /* Frost crystals */
  if(temp.frost){
    const crystals = [
      [0.08,0.12],[0.22,0.05],[0.4,0.11],[0.58,0.04],[0.72,0.14],[0.88,0.08],
      [0.12,0.92],[0.3,0.95],[0.48,0.88],[0.66,0.94],[0.82,0.9],[0.94,0.86],
      [0.05,0.5],[0.95,0.48]
    ];
    crystals.forEach(([fx,fy])=>{
      out += `<text x="${x+w*fx}" y="${y+h*fy}" text-anchor="middle"
        font-size="9" fill="#FFFFFF" opacity="0.75">❄</text>`;
    });
  }

  /* Temperature icon */
  if(temp.icon){
    const iconColor = s.temp === 'frozen' ? '#DBEAFE' : '#93C5FD';
    out += `<circle cx="${x+w-13}" cy="${y+13}" r="9" fill="rgba(0,0,0,0.3)"/>`;
    out += `<text x="${x+w-13}" y="${y+16}" text-anchor="middle" font-size="11"
      fill="${iconColor}">${temp.icon}</text>`;
  }

  /* Glass sheen */
  out += `<rect x="${x}" y="${y}" width="${w*0.5}" height="${h}" fill="url(#glassSheen)" opacity="0.6"/>`;
  out += `<rect x="${x}" y="${y}" width="${w}" height="${h}" fill="none"
    stroke="rgba(255,255,255,0.1)" stroke-width="1"/>`;

  out += `</g>`;
  return out;
}

function getRowType(r, totalRows){
  const patterns = {
    5: ['snack','candy','bottle','bottle','can'],
    6: ['snack','candy','bottle','bottle','can','water']
  };
  return (patterns[totalRows] || patterns[5])[r];
}

/* ═══════════════════════════════════════════════════════════════════════
   PRODUCTS + DELIVERY MECHANISMS
   ═══════════════════════════════════════════════════════════════════════ */
function drawProduct(type, delivery, slotX, slotY, slotW, slotH, c, row, col){
  const padX = slotW * 0.12;
  const padY = slotH * 0.1;
  const pX = slotX + padX;
  const pY = slotY + padY;
  const pW = slotW - padX*2;
  const pH = slotH - padY*2 - 6;

  let prod = '';
  if(type === 'snack')  prod = drawSnackBag(pX, pY, pW, pH, c);
  else if(type === 'candy') prod = drawCandyBar(pX, pY, pW, pH, c);
  else if(type === 'bottle') prod = drawBottle(pX, pY, pW, pH, c);
  else if(type === 'can') prod = drawCan(pX, pY, pW, pH, c);
  else if(type === 'water') prod = drawWater(pX, pY, pW, pH, c);

  let mech = '';
  const mechY = slotY + slotH - 5;

  if(delivery === 'coil' || (delivery === 'mixed' && row < 2)){
    const coilW = pW * 1.1;
    const coilX = pX - pW*0.05;
    mech = `<path d="M${coilX},${mechY}
      Q${coilX+coilW*0.12},${mechY-3} ${coilX+coilW*0.25},${mechY}
      T${coilX+coilW*0.5},${mechY}
      T${coilX+coilW*0.75},${mechY}
      T${coilX+coilW},${mechY}"
      fill="none" stroke="rgba(255,255,255,0.55)" stroke-width="1.3" stroke-linecap="round"/>`;
  } else if(delivery === 'push' || (delivery === 'mixed' && row >= 2)){
    const pushX1 = pX + pW*0.3, pushX2 = pX + pW*0.7;
    mech = `<line x1="${pushX1}" y1="${mechY-5}" x2="${pushX1}" y2="${mechY+1}"
      stroke="rgba(255,255,255,0.55)" stroke-width="1.6" stroke-linecap="round"/>
      <circle cx="${pushX1}" cy="${mechY-6}" r="1.3" fill="rgba(255,255,255,0.6)"/>
      <line x1="${pushX2}" y1="${mechY-5}" x2="${pushX2}" y2="${mechY+1}"
      stroke="rgba(255,255,255,0.55)" stroke-width="1.6" stroke-linecap="round"/>
      <circle cx="${pushX2}" cy="${mechY-6}" r="1.3" fill="rgba(255,255,255,0.6)"/>`;
  } else if(delivery === 'conveyor'){
    mech = `<rect x="${pX-1}" y="${mechY-2}" width="${pW+2}" height="3" rx="1"
      fill="rgba(255,255,255,0.15)" stroke="rgba(255,255,255,0.4)" stroke-width="0.4"/>
      <polygon points="${pX+pW*0.3},${mechY-0.5} ${pX+pW*0.4},${mechY-0.5} ${pX+pW*0.38},${mechY+0.3} ${pX+pW*0.28},${mechY+0.3}"
      fill="rgba(255,255,255,0.7)"/>
      <polygon points="${pX+pW*0.55},${mechY-0.5} ${pX+pW*0.65},${mechY-0.5} ${pX+pW*0.63},${mechY+0.3} ${pX+pW*0.53},${mechY+0.3}"
      fill="rgba(255,255,255,0.7)"/>`;
  } else if(delivery === 'elevator'){
    mech = `<line x1="${slotX+1}" y1="${slotY}" x2="${slotX+1}" y2="${slotY+slotH}"
      stroke="rgba(147,197,253,0.5)" stroke-width="0.6" stroke-dasharray="2,1.5"/>`;
  }

  return prod + mech;
}

function drawSnackBag(x, y, w, h, c){
  const cornerR = Math.min(w, h) * 0.12;
  return `<g>
    <path d="M${x+cornerR},${y-1} Q${x+w*0.3},${y+2} ${x+w*0.5},${y-1}
             Q${x+w*0.7},${y+2} ${x+w-cornerR},${y-1}
             Q${x+w},${y-1} ${x+w},${y+cornerR}
             L${x+w},${y+h-2} Q${x+w},${y+h} ${x+w-2},${y+h}
             L${x+2},${y+h} Q${x},${y+h} ${x},${y+h-2}
             L${x},${y+cornerR} Q${x},${y-1} ${x+cornerR},${y-1} Z"
          fill="${c.body}" stroke="rgba(0,0,0,0.12)" stroke-width="0.4"/>
    <path d="M${x+cornerR},${y-1} Q${x+w*0.3},${y+2} ${x+w*0.5},${y-1}
             Q${x+w*0.7},${y+2} ${x+w-cornerR},${y-1}"
          fill="none" stroke="rgba(255,255,255,0.5)" stroke-width="0.5"/>
    <rect x="${x+1}" y="${y+h*0.32}" width="${w-2}" height="${h*0.32}" fill="${c.label}" opacity="0.95"/>
    <rect x="${x+w*0.18}" y="${y+h*0.4}" width="${w*0.64}" height="${h*0.08}" fill="${c.name}" opacity="0.85"/>
    <rect x="${x+w*0.25}" y="${y+h*0.52}" width="${w*0.5}" height="${h*0.05}" fill="${c.name}" opacity="0.55"/>
    <rect x="${x+1}" y="${y+cornerR}" width="${w*0.15}" height="${h-cornerR*2}" fill="rgba(255,255,255,0.12)"/>
  </g>`;
}

function drawCandyBar(x, y, w, h, c){
  const mx = x + w*0.05, mw = w*0.9;
  const my = y + h*0.18, mh = h*0.72;
  return `<g>
    <rect x="${mx}" y="${my}" width="${mw}" height="${mh}" rx="1.5" fill="${c.body}"
      stroke="rgba(0,0,0,0.15)" stroke-width="0.4"/>
    <path d="M${mx-1},${my+mh*0.2} L${mx},${my+mh*0.3} L${mx},${my+mh*0.7} L${mx-1},${my+mh*0.8} Z"
      fill="rgba(0,0,0,0.2)"/>
    <path d="M${mx+mw+1},${my+mh*0.2} L${mx+mw},${my+mh*0.3} L${mx+mw},${my+mh*0.7} L${mx+mw+1},${my+mh*0.8} Z"
      fill="rgba(0,0,0,0.2)"/>
    <rect x="${mx+2}" y="${my+mh*0.3}" width="${mw-4}" height="${mh*0.35}" fill="${c.label}" opacity="0.95"/>
    <rect x="${mx+4}" y="${my+mh*0.38}" width="${mw-8}" height="${mh*0.15}" fill="${c.name}" opacity="0.8"/>
    <rect x="${mx+1}" y="${my}" width="${mw-2}" height="${mh*0.18}" fill="rgba(255,255,255,0.18)"/>
  </g>`;
}

function drawBottle(x, y, w, h, c){
  const bodyW = w * 0.78;
  const bX = x + (w - bodyW)/2;
  const capW = w * 0.32;
  const capX = x + (w-capW)/2;
  const capH = h * 0.07;
  const neckH = h * 0.08;
  return `<g>
    <rect x="${capX}" y="${y}" width="${capW}" height="${capH}" fill="#4B5563" rx="0.8"/>
    <rect x="${capX}" y="${y+capH-1.5}" width="${capW}" height="1.5" fill="#1F2937"/>
    <rect x="${capX+1}" y="${y+capH}" width="${capW-2}" height="${neckH*0.5}" fill="#9CA3AF"/>
    <path d="M${capX+1},${y+capH+neckH*0.5} L${bX},${y+capH+neckH}
             L${bX},${y+h-2} Q${bX},${y+h} ${bX+2},${y+h}
             L${bX+bodyW-2},${y+h} Q${bX+bodyW},${y+h} ${bX+bodyW},${y+h-2}
             L${bX+bodyW},${y+capH+neckH} L${capX+capW-1},${y+capH+neckH*0.5} Z"
          fill="${c.body}" stroke="rgba(0,0,0,0.15)" stroke-width="0.4"/>
    <rect x="${bX}" y="${y+h*0.38}" width="${bodyW}" height="${h*0.34}" fill="${c.label}" opacity="0.95"/>
    <rect x="${bX+2}" y="${y+h*0.45}" width="${bodyW-4}" height="${h*0.08}" fill="${c.name}" opacity="0.85"/>
    <rect x="${bX+4}" y="${y+h*0.58}" width="${bodyW-8}" height="${h*0.05}" fill="${c.name}" opacity="0.5"/>
    <rect x="${bX+1.5}" y="${y+capH+neckH+1}" width="${bodyW*0.2}" height="${h-capH-neckH-3}"
      fill="rgba(255,255,255,0.16)"/>
    <ellipse cx="${bX+bodyW/2}" cy="${y+h-1}" rx="${bodyW/2}" ry="1.5" fill="rgba(0,0,0,0.3)"/>
  </g>`;
}

function drawCan(x, y, w, h, c){
  const bodyW = w * 0.82;
  const bX = x + (w-bodyW)/2;
  return `<g>
    <rect x="${bX}" y="${y+2}" width="${bodyW}" height="${h-3}" rx="2" fill="${c.body}"
      stroke="rgba(0,0,0,0.15)" stroke-width="0.4"/>
    <rect x="${bX}" y="${y}" width="${bodyW}" height="3" rx="1" fill="#9CA3AF"/>
    <rect x="${bX+1}" y="${y+1.5}" width="${bodyW-2}" height="1" fill="#4B5563"/>
    <ellipse cx="${bX+bodyW*0.6}" cy="${y+2}" rx="${bodyW*0.15}" ry="1" fill="#374151"/>
    <rect x="${bX}" y="${y+h-4}" width="${bodyW}" height="3" fill="rgba(0,0,0,0.3)"/>
    <rect x="${bX}" y="${y+h*0.3}" width="${bodyW}" height="${h*0.42}" fill="${c.label}" opacity="0.95"/>
    <rect x="${bX+2}" y="${y+h*0.38}" width="${bodyW-4}" height="${h*0.1}" fill="${c.name}" opacity="0.85"/>
    <rect x="${bX+4}" y="${y+h*0.55}" width="${bodyW-8}" height="${h*0.05}" fill="${c.name}" opacity="0.55"/>
    <rect x="${bX+1}" y="${y+4}" width="${bodyW*0.18}" height="${h-8}" fill="rgba(255,255,255,0.2)"/>
    <rect x="${bX+bodyW-bodyW*0.12}" y="${y+4}" width="${bodyW*0.1}" height="${h-8}" fill="rgba(0,0,0,0.15)"/>
  </g>`;
}

function drawWater(x, y, w, h, c){
  const bodyW = w * 0.62;
  const bX = x + (w - bodyW)/2;
  const capW = w * 0.25;
  const capX = x + (w-capW)/2;
  const capH = h * 0.08;
  return `<g>
    <rect x="${capX}" y="${y}" width="${capW}" height="${capH}" fill="#1D4ED8" rx="1"/>
    <rect x="${capX+0.5}" y="${y+capH}" width="${capW-1}" height="${h*0.06}" fill="#60A5FA"/>
    <path d="M${capX},${y+capH+h*0.06} L${bX},${y+capH+h*0.1}
             L${bX},${y+h-3} Q${bX},${y+h} ${bX+2},${y+h}
             L${bX+bodyW-2},${y+h} Q${bX+bodyW},${y+h} ${bX+bodyW},${y+h-3}
             L${bX+bodyW},${y+capH+h*0.1} L${capX+capW},${y+capH+h*0.06} Z"
          fill="${c.body}" stroke="rgba(59,130,246,0.3)" stroke-width="0.4"/>
    <rect x="${bX+1}" y="${y+h*0.42}" width="${bodyW-2}" height="${h*0.28}" fill="#FFFFFF" opacity="0.95"/>
    <rect x="${bX+3}" y="${y+h*0.5}" width="${bodyW-6}" height="${h*0.08}" fill="${c.label}" opacity="0.9"/>
    <line x1="${bX+bodyW*0.25}" y1="${y+h*0.78}" x2="${bX+bodyW*0.75}" y2="${y+h*0.78}"
      stroke="${c.name}" stroke-width="0.5" opacity="0.4"/>
    <rect x="${bX+1}" y="${y+capH+h*0.1+1}" width="${bodyW*0.25}" height="${h-capH-h*0.1-3}"
      fill="rgba(255,255,255,0.3)"/>
  </g>`;
}

/* ═══════════════════════════════════════════════════════════════════════
   CONTROL PANEL ROUTER
   ═══════════════════════════════════════════════════════════════════════ */
function buildControlPanel(s, x, y, w, h){
  let inner;
  if(s.system === 'smart' || s.screen === 'large_touch'){
    inner = buildSmartControlPanel(s, x, y, w, h);
  } else if(s.screen === 'medium_touch'){
    inner = buildMediumControlPanel(s, x, y, w, h);
  } else {
    inner = buildClassicControlPanel(s, x, y, w, h);
  }
  const isFullWrap = s.wrap === 'full';
  if(isFullWrap){
    const container = `<rect x="${x-4}" y="${y-4}" width="${w+8}" height="${h+8}" rx="4"
      fill="#FFFFFF" stroke="#E5E7EB" stroke-width="0.8"/>`;
    return container + inner;
  }
  return inner;
}

/* ═══════════════════════════════════════════════════════════════════════
   CLASSIC CONTROL PANEL
   ═══════════════════════════════════════════════════════════════════════ */
function buildClassicControlPanel(s, x, y, w, h){
  let out = `<g id="classic-ctrl">`;

  out += `<rect x="${x}" y="${y}" width="${w}" height="22" rx="2" fill="#F3F4F6"
    stroke="#E5E7EB" stroke-width="0.5"/>`;
  out += `<text x="${x+w/2}" y="${y+13}" text-anchor="middle"
    font-family="-apple-system,sans-serif" font-size="8" font-weight="900"
    letter-spacing="1.5" fill="#1F2937">VMFS USA™</text>`;
  out += `<text x="${x+w/2}" y="${y+20}" text-anchor="middle"
    font-family="-apple-system,sans-serif" font-size="4.5" font-weight="600"
    letter-spacing="1.2" fill="#6B7280">VENDING MACHINES</text>`;

  let cy = y + 28;

  if(s.ageverify){
    const avH = 40;
    out += S.buildIDScannerPanel(x, cy, w, avH);
    cy += avH + 6;
  }

  /* SMALL LCD */
  const lcdH = 22;
  out += `<rect x="${x}" y="${cy}" width="${w}" height="${lcdH}" rx="3" fill="#111827"
    stroke="#0A0F1C" stroke-width="0.8"/>`;
  out += `<rect x="${x+2}" y="${cy+2}" width="${w-4}" height="${lcdH-4}" rx="2" fill="#052E16"/>`;
  out += `<text x="${x+w/2}" y="${cy+10}" text-anchor="middle"
    font-family="Courier New,monospace" font-size="5.5" font-weight="700" fill="#86EFAC"
    letter-spacing="0.8">ENTER CODE</text>`;
  out += `<text x="${x+w/2}" y="${cy+18}" text-anchor="middle"
    font-family="Courier New,monospace" font-size="8" font-weight="900" fill="#4ADE80"
    letter-spacing="2">A·1</text>`;
  out += `<circle cx="${x+w-7}" cy="${cy+5}" r="1.2" fill="#86EFAC"/>`;
  cy += lcdH + 5;

  /* NUMERIC KEYPAD */
  const kpadRows = 4, kpadCols = 3;
  const kW = (w - (kpadCols-1)*2.5) / kpadCols;
  const kH = 13;
  const keys = [['1','2','3'],['4','5','6'],['7','8','9'],['*','0','#']];
  for(let r=0;r<kpadRows;r++){
    for(let col=0;col<kpadCols;col++){
      const kx = x + col * (kW + 2.5);
      const ky = cy + r * (kH + 2.5);
      out += `<rect x="${kx}" y="${ky}" width="${kW}" height="${kH}" rx="2.5"
        fill="#FFFFFF" stroke="#D1D5DB" stroke-width="0.7"/>`;
      out += `<rect x="${kx+0.5}" y="${ky+0.5}" width="${kW-1}" height="${kH-1}" rx="2"
        fill="none" stroke="rgba(255,255,255,0.6)" stroke-width="0.4"/>`;
      out += `<text x="${kx+kW/2}" y="${ky+kH*0.72}" text-anchor="middle"
        font-family="-apple-system,sans-serif" font-size="7.5" font-weight="800"
        fill="#1F2937">${keys[r][col]}</text>`;
    }
  }
  cy += kpadRows * (kH+2.5) + 6;

  out += S.buildPaymentRow(s, x, cy, w);
  cy += 32;

  if((s.payments||[]).includes('bill_coin')){
    out += S.buildCoinReturn(x, cy, w);
  }

  out += `</g>`;
  return out;
}

/* ═══════════════════════════════════════════════════════════════════════
   SMART CONTROL PANEL
   ═══════════════════════════════════════════════════════════════════════ */
function buildSmartControlPanel(s, x, y, w, h){
  let out = `<g id="smart-ctrl">`;

  out += `<rect x="${x}" y="${y}" width="${w}" height="26" fill="#F9FAFB" rx="3"
    stroke="#E5E7EB" stroke-width="0.5"/>`;
  out += `<text x="${x+w/2}" y="${y+13}" text-anchor="middle"
    font-family="-apple-system,sans-serif" font-size="9" font-weight="900"
    letter-spacing="1.2" fill="#1F2937">VMFS USA™</text>`;
  out += `<text x="${x+w/2}" y="${y+22}" text-anchor="middle"
    font-family="-apple-system,sans-serif" font-size="5" font-weight="700"
    letter-spacing="1.5" fill="#1A56DB">SMART · AI · CONNECTED</text>`;

  let cy = y + 32;

  if(s.ageverify){
    const avH = 42;
    out += S.buildIDScannerPanel(x, cy, w, avH);
    cy += avH + 6;
  }

  /* LARGE touchscreen */
  const tsH = s.ageverify ? 96 : 126;
  out += `<rect x="${x}" y="${cy}" width="${w}" height="${tsH}" rx="5" fill="#111827"/>`;
  out += `<rect x="${x+2}" y="${cy+2}" width="${w-4}" height="${tsH-4}" rx="4" fill="#F9FAFB"/>`;
  out += `<circle cx="${x+w/2}" cy="${cy+1.3}" r="0.7" fill="#4B5563"/>`;
  out += buildSmartScreenContent(x+4, cy+4, w-8, tsH-8, s);
  out += `<rect x="${x+2}" y="${cy+2}" width="${w-4}" height="${(tsH-4)*0.4}" rx="4" fill="rgba(255,255,255,0.1)"/>`;
  cy += tsH + 8;

  /* Modern card reader */
  if((s.payments||[]).includes('tap_swipe')){
    const crH = 24;
    out += `<rect x="${x}" y="${cy}" width="${w}" height="${crH}" rx="4" fill="#1F2937"/>`;
    out += `<rect x="${x+3}" y="${cy+3}" width="${w-6}" height="${crH-6}" rx="3" fill="#111827"
      stroke="#1A56DB" stroke-width="0.8"/>`;
    out += `<circle cx="${x+w*0.3}" cy="${cy+crH/2}" r="5" fill="none" stroke="#1A56DB" stroke-width="0.8" opacity="0.8"/>`;
    out += `<circle cx="${x+w*0.3}" cy="${cy+crH/2}" r="3" fill="none" stroke="#1A56DB" stroke-width="0.8" opacity="0.6"/>`;
    out += `<circle cx="${x+w*0.3}" cy="${cy+crH/2}" r="1" fill="#1A56DB"/>`;
    out += `<rect x="${x+w*0.5}" y="${cy+4}" width="${w*0.4}" height="2" fill="#1A56DB" opacity="0.6"/>`;
    out += `<text x="${x+w*0.5}" y="${cy+crH-5}" font-family="-apple-system,sans-serif"
      font-size="5" font-weight="700" fill="#E5E7EB" letter-spacing="1">TAP · SWIPE · INSERT</text>`;
    cy += crH + 5;
  }

  /* Cash/coin module */
  if((s.payments||[]).includes('bill_coin')){
    const cmH = 30;
    out += `<rect x="${x}" y="${cy}" width="${w}" height="${cmH}" rx="4" fill="#111827"/>`;
    out += `<rect x="${x+4}" y="${cy+4}" width="${w-8}" height="10" rx="2" fill="#FBBF24"
      stroke="#B45309" stroke-width="0.5"/>`;
    out += `<rect x="${x+8}" y="${cy+7}" width="${w-16}" height="3" rx="1" fill="#78350F"/>`;
    out += `<text x="${x+w/2}" y="${cy+13}" text-anchor="middle" font-family="-apple-system,sans-serif"
      font-size="4" font-weight="800" fill="#78350F">INSERT BILL →</text>`;
    out += `<circle cx="${x+w*0.25}" cy="${cy+22}" r="4" fill="#374151" stroke="#6B7280" stroke-width="0.5"/>`;
    out += `<rect x="${x+w*0.25-0.6}" y="${cy+18}" width="1.2" height="8" fill="#111827"/>`;
    out += `<path d="M${x+w*0.55},${cy+19} L${x+w*0.85},${cy+19} L${x+w*0.82},${cy+27} L${x+w*0.58},${cy+27} Z"
      fill="#374151" stroke="#6B7280" stroke-width="0.5"/>`;
    out += `<text x="${x+w*0.7}" y="${cy+25}" text-anchor="middle" font-family="-apple-system,sans-serif"
      font-size="3.5" font-weight="700" fill="#D1D5DB">RETURN</text>`;
    cy += cmH + 4;
  }

  if((s.payments||[]).includes('codes') && !((s.payments||[]).includes('tap_swipe') || (s.payments||[]).includes('bill_coin'))){
    out += S.buildPaymentRow(s, x, cy, w);
  }

  out += `</g>`;
  return out;
}

/* ═══════════════════════════════════════════════════════════════════════
   MEDIUM CONTROL PANEL
   ═══════════════════════════════════════════════════════════════════════ */
function buildMediumControlPanel(s, x, y, w, h){
  let out = `<g id="medium-ctrl">`;

  out += `<rect x="${x}" y="${y}" width="${w}" height="22" fill="#F3F4F6" rx="2"
    stroke="#E5E7EB" stroke-width="0.5"/>`;
  out += `<text x="${x+w/2}" y="${y+13}" text-anchor="middle"
    font-family="-apple-system,sans-serif" font-size="8" font-weight="900"
    letter-spacing="1.2" fill="#1F2937">VMFS USA</text>`;
  out += `<text x="${x+w/2}" y="${y+20}" text-anchor="middle"
    font-family="-apple-system,sans-serif" font-size="4.5" font-weight="600"
    letter-spacing="1" fill="#6B7280">VENDING MACHINES</text>`;

  let cy = y + 28;

  if(s.ageverify){
    const avH = 40;
    out += S.buildIDScannerPanel(x, cy, w, avH);
    cy += avH + 5;
  }

  const tsH = 68;
  out += `<rect x="${x}" y="${cy}" width="${w}" height="${tsH}" rx="4" fill="#111827"/>`;
  out += `<rect x="${x+2}" y="${cy+2}" width="${w-4}" height="${tsH-4}" rx="3" fill="#F9FAFB"/>`;
  out += `<circle cx="${x+w/2}" cy="${cy+1.3}" r="0.6" fill="#4B5563"/>`;
  out += buildSmartScreenContent(x+3, cy+3, w-6, tsH-6, s);
  cy += tsH + 5;

  /* Compact keypad */
  const kpadRows = 2, kpadCols = 3;
  const kW = (w - (kpadCols-1)*2) / kpadCols;
  const kH = 10;
  const keys = [['1','2','3'],['4','5','6']];
  for(let r=0;r<kpadRows;r++){
    for(let col=0;col<kpadCols;col++){
      const kx = x + col * (kW + 2);
      const ky = cy + r * (kH + 2);
      out += `<rect x="${kx}" y="${ky}" width="${kW}" height="${kH}" rx="2"
        fill="#FFFFFF" stroke="#D1D5DB" stroke-width="0.6"/>`;
      out += `<text x="${kx+kW/2}" y="${ky+kH*0.72}" text-anchor="middle"
        font-family="-apple-system,sans-serif" font-size="6" font-weight="800"
        fill="#1F2937">${keys[r][col]}</text>`;
    }
  }
  cy += kpadRows * (kH+2) + 5;

  out += S.buildPaymentRow(s, x, cy, w);
  cy += 32;
  if((s.payments||[]).includes('bill_coin')){
    out += S.buildCoinReturn(x, cy, w);
  }

  out += `</g>`;
  return out;
}

/* ═══════════════════════════════════════════════════════════════════════
   SMART SCREEN CONTENT (touchscreen UI mockup)
   ═══════════════════════════════════════════════════════════════════════ */
function buildSmartScreenContent(x, y, w, h, s){
  const sysAccent = s.system === 'advertising' ? '#F97316' : '#1A56DB';
  let out = `<g>`;

  /* Top header with flag stripes */
  const hdrH = 14;
  out += `<rect x="${x}" y="${y}" width="${w}" height="${hdrH}" fill="#DC2626"/>`;
  for(let i=0;i<6;i++){
    out += `<rect x="${x}" y="${y+i*2.3}" width="${w*0.55}" height="1.2" fill="#FFFFFF" opacity="0.7"/>`;
  }
  out += `<rect x="${x}" y="${y}" width="${w*0.22}" height="${hdrH*0.6}" fill="#1D4ED8"/>`;
  for(let i=0;i<3;i++){
    for(let j=0;j<5;j++){
      out += `<circle cx="${x+w*0.04+j*w*0.035}" cy="${y+2.5+i*2.5}" r="0.5" fill="#FFFFFF"/>`;
    }
  }

  /* Product tiles grid */
  const gridY = y + hdrH + 4;
  const gridH = h - hdrH - 18;
  const cols = 4, rows = 2;
  const tileW = (w - 10 - (cols-1)*3) / cols;
  const tileH = (gridH - (rows-1)*3) / rows;

  const tilePalette = ['#F97316','#DC2626','#16A34A','#1E40AF','#7C3AED','#EC4899','#F59E0B','#0891B2'];

  for(let r=0;r<rows;r++){
    for(let c=0;c<cols;c++){
      const tx = x + 5 + c*(tileW+3);
      const ty = gridY + r*(tileH+3);
      const color = tilePalette[(r*cols+c) % tilePalette.length];
      out += `<rect x="${tx}" y="${ty}" width="${tileW}" height="${tileH}" rx="2"
        fill="${color}" opacity="0.85"/>`;
      out += `<rect x="${tx+1}" y="${ty+tileH-6}" width="${tileW-2}" height="5" fill="rgba(255,255,255,0.95)"/>`;
      out += `<text x="${tx+tileW/2}" y="${ty+tileH-2}" text-anchor="middle"
        font-family="-apple-system,sans-serif" font-size="3.2" font-weight="800" fill="#1F2937">$${(1.5+c*0.5).toFixed(2)}</text>`;
    }
  }

  /* Bottom bar */
  out += `<rect x="${x}" y="${y+h-10}" width="${w}" height="10" fill="${sysAccent}"/>`;
  out += `<text x="${x+5}" y="${y+h-3}" font-family="-apple-system,sans-serif" font-size="5"
    font-weight="700" fill="#FFFFFF">READY</text>`;
  out += `<circle cx="${x+w-6}" cy="${y+h-5}" r="1.5" fill="#86EFAC"/>`;

  out += `</g>`;
  return out;
}

/* ═══════════════════════════════════════════════════════════════════════
   ADVERTISING MACHINE BODY
   ═══════════════════════════════════════════════════════════════════════ */
function buildAdvertisingBody(s, sz, mX, mY, mW, mH){
  const bodyCol = S.getBodyColor(s);
  const isFullWrap = s.wrap === 'full' && s.wrapColor;

  let out = `<g id="ad-body" filter="url(#bodyShadow)">`;

  out += `<rect x="${mX}" y="${mY}" width="${mW}" height="${mH}" rx="6"
    fill="${isFullWrap ? bodyCol : 'url(#bodyShell)'}"
    stroke="${S.getBodyEdgeColor(s)}" stroke-width="1.2"/>`;
  out += `<rect x="${mX+1.5}" y="${mY+1.5}" width="${mW-3}" height="${mH-3}" rx="4.5"
    fill="none" stroke="rgba(0,0,0,0.04)" stroke-width="1"/>`;

  const stripW = 44;
  const stripX = mX + mW - stripW - 8;

  const scrX = mX + 10;
  const scrW = mW - stripW - 24;
  const scrY = mY + 14;
  const scrH = mH * 0.68;

  out += buildAdScreen(scrX, scrY, scrW, scrH, s);

  const logoY = scrY + scrH + 16;
  out += S.buildVmfsLogoBig(mX + scrW/2 + 10, logoY, isFullWrap && bodyCol !== '#FFFFFF' ? '#FFFFFF' : '#1F2937');

  const pushW = Math.min(scrW * 0.4, 90);
  const pushX = scrX + (scrW - pushW)/2;
  const pushY = mY + mH - 44;
  out += S.buildDeliveryDoor(pushX, pushY, pushW, 30, false);

  out += buildAdPaymentStrip(s, stripX, scrY, stripW, scrH);

  out += `</g>`;
  return out;
}

function buildAdScreen(x, y, w, h, s){
  const temp = TEMP_THEME[s.temp] || TEMP_THEME.ambient;
  let out = `<g id="ad-screen">`;
  out += `<rect x="${x-2}" y="${y-2}" width="${w+4}" height="${h+4}" rx="4" fill="#0A0F1C"/>`;
  out += `<rect x="${x}" y="${y}" width="${w}" height="${h}" rx="2" fill="url(#ledScreen)"/>`;
  out += `<rect x="${x}" y="${y}" width="${w}" height="${h}" rx="2" fill="url(#adSpotlight)"/>`;

  const bx = x + w*0.38, by = y + h*0.22, bw = w*0.24, bh = h*0.58;
  out += `<circle cx="${x+w*0.22}" cy="${y+h*0.32}" r="${w*0.09}" fill="#DC2626" opacity="0.92"/>`;
  out += `<circle cx="${x+w*0.2}" cy="${y+h*0.3}" r="${w*0.07}" fill="#F87171" opacity="0.5"/>`;
  out += `<circle cx="${x+w*0.78}" cy="${y+h*0.28}" r="${w*0.08}" fill="#FBBF24" opacity="0.92"/>`;
  out += `<circle cx="${x+w*0.76}" cy="${y+h*0.26}" r="${w*0.05}" fill="#FDE68A" opacity="0.5"/>`;
  out += `<ellipse cx="${x+w*0.2}" cy="${y+h*0.65}" rx="${w*0.1}" ry="${w*0.07}" fill="#16A34A" opacity="0.85"/>`;
  out += `<circle cx="${x+w*0.82}" cy="${y+h*0.7}" r="${w*0.07}" fill="#EA580C" opacity="0.9"/>`;
  out += `<ellipse cx="${x+w*0.5}" cy="${y+h*0.58}" rx="${w*0.32}" ry="${h*0.12}"
    fill="rgba(255,255,255,0.45)"/>`;

  out += `<rect x="${bx+bw*0.35}" y="${by-h*0.04}" width="${bw*0.3}" height="${h*0.05}" fill="#1F2937" rx="1"/>`;
  out += `<rect x="${bx+bw*0.38}" y="${by-h*0.01}" width="${bw*0.24}" height="${h*0.05}" fill="#9CA3AF"/>`;
  out += `<path d="M${bx+bw*0.38},${by+h*0.04} L${bx},${by+h*0.12}
           L${bx},${by+bh*0.95} Q${bx},${by+bh} ${bx+3},${by+bh}
           L${bx+bw-3},${by+bh} Q${bx+bw},${by+bh} ${bx+bw},${by+bh*0.95}
           L${bx+bw},${by+h*0.12} L${bx+bw*0.62},${by+h*0.04} Z"
        fill="#F97316" stroke="rgba(0,0,0,0.15)" stroke-width="0.6"/>`;
  out += `<rect x="${bx+2}" y="${by+bh*0.35}" width="${bw-4}" height="${bh*0.3}" fill="#FEF3C7"/>`;
  out += `<text x="${bx+bw/2}" y="${by+bh*0.48}" text-anchor="middle"
    font-family="-apple-system,sans-serif" font-size="${bw*0.18}" font-weight="900"
    fill="#DC2626">JUICE</text>`;
  out += `<text x="${bx+bw/2}" y="${by+bh*0.57}" text-anchor="middle"
    font-family="-apple-system,sans-serif" font-size="${bw*0.1}" font-weight="700"
    fill="#92400E">MANGO</text>`;

  const wmX = x + w*0.5, wmY = y + h*0.5, wmR = w*0.13;
  out += `<circle cx="${wmX}" cy="${wmY}" r="${wmR}" fill="none" stroke="#FFFFFF" stroke-width="2" opacity="0.45"/>`;
  out += `<text x="${wmX}" y="${wmY+wmR*0.3}" text-anchor="middle"
    font-family="-apple-system,sans-serif" font-size="${wmR*0.9}" font-weight="900"
    fill="#FFFFFF" opacity="0.5">V</text>`;

  out += `<text x="${x+8}" y="${y+14}" font-family="-apple-system,sans-serif"
    font-size="7" font-weight="800" letter-spacing="1" fill="#FFFFFF" opacity="0.85">VMFS LIVE</text>`;
  out += `<circle cx="${x+w-10}" cy="${y+11}" r="2.5" fill="#EF4444" filter="url(#glowSm)"/>`;
  out += `<text x="${x+w-16}" y="${y+14}" text-anchor="end" font-family="-apple-system,sans-serif"
    font-size="6" font-weight="700" fill="#FFFFFF" opacity="0.9">ON AIR</text>`;

  if(s.ageverify){
    out += `<rect x="${x+8}" y="${y+h-28}" width="60" height="20" rx="10" fill="#DC2626" opacity="0.95"/>`;
    out += `<text x="${x+38}" y="${y+h-14}" text-anchor="middle"
      font-family="-apple-system,sans-serif" font-size="9" font-weight="900"
      fill="#FFFFFF">21+ ONLY</text>`;
  }

  if(s.temp === 'cooled' || s.temp === 'frozen'){
    out += `<rect x="${x}" y="${y}" width="${w}" height="${h}" fill="${temp.overlay}" opacity="0.4"/>`;
  }

  for(let i=0;i<Math.floor(h/4);i++){
    out += `<rect x="${x}" y="${y+i*4}" width="${w}" height="0.5" fill="rgba(0,0,0,0.06)"/>`;
  }

  out += `</g>`;
  return out;
}

function buildAdPaymentStrip(s, x, y, w, h){
  const hasCard = (s.payments||[]).includes('tap_swipe');
  const hasCash = (s.payments||[]).includes('bill_coin');
  const hasQR   = (s.payments||[]).includes('codes');

  let out = `<g id="ad-pay-strip">`;
  out += `<rect x="${x}" y="${y}" width="${w}" height="${h}" rx="3" fill="#F9FAFB"
    stroke="#D1D5DB" stroke-width="0.8"/>`;

  let cy = y + 8;
  const slotH = 40;

  if(hasCard){
    out += `<rect x="${x+4}" y="${cy}" width="${w-8}" height="${slotH-4}" rx="3" fill="#111827"/>`;
    out += `<rect x="${x+7}" y="${cy+4}" width="${w-14}" height="14" rx="1.5" fill="#1F2937"
      stroke="#4A9EFF" stroke-width="0.5"/>`;
    out += `<rect x="${x+8}" y="${cy+6}" width="${w-16}" height="2" fill="#3B82F6" opacity="0.6"/>`;
    out += `<text x="${x+w/2}" y="${cy+slotH-8}" text-anchor="middle"
      font-family="-apple-system,sans-serif" font-size="4.5" font-weight="700"
      fill="#E5E7EB">CARD</text>`;
    cy += slotH;
  }

  if(hasCash){
    out += `<rect x="${x+4}" y="${cy}" width="${w-8}" height="${slotH-4}" rx="3" fill="#111827"/>`;
    out += `<rect x="${x+7}" y="${cy+5}" width="${w-14}" height="3" fill="#374151"/>`;
    out += `<circle cx="${x+w/2}" cy="${cy+20}" r="4" fill="#374151" stroke="#6B7280" stroke-width="0.5"/>`;
    out += `<rect x="${x+w/2-0.6}" y="${cy+16}" width="1.2" height="8" fill="#111827"/>`;
    out += `<text x="${x+w/2}" y="${cy+slotH-6}" text-anchor="middle"
      font-family="-apple-system,sans-serif" font-size="4.5" font-weight="700"
      fill="#E5E7EB">CASH</text>`;
    cy += slotH;
  }

  if(hasQR){
    out += `<rect x="${x+4}" y="${cy}" width="${w-8}" height="${slotH-4}" rx="3" fill="#111827"/>`;
    const qcx = x+w/2, qcy = cy+14;
    for(let gx=0;gx<3;gx++){
      for(let gy=0;gy<3;gy++){
        if(!(gx===1 && gy===1)){
          out += `<rect x="${qcx-6+gx*4.5}" y="${qcy-6+gy*4.5}" width="2" height="2"
            fill="#3B82F6" opacity="0.9"/>`;
        }
      }
    }
    out += `<text x="${x+w/2}" y="${cy+slotH-6}" text-anchor="middle"
      font-family="-apple-system,sans-serif" font-size="4.5" font-weight="700"
      fill="#E5E7EB">QR</text>`;
    cy += slotH;
  }

  if(s.ageverify && cy < y + h - 35){
    out += `<rect x="${x+4}" y="${cy}" width="${w-8}" height="30" rx="3" fill="#FEF3C7"
      stroke="#DC2626" stroke-width="0.8"/>`;
    out += `<rect x="${x+7}" y="${cy+3}" width="${w-14}" height="2" rx="1" fill="#DC2626"/>`;
    out += `<text x="${x+w/2}" y="${cy+15}" text-anchor="middle"
      font-family="-apple-system,sans-serif" font-size="7" font-weight="900"
      fill="#DC2626">21+</text>`;
    out += `<text x="${x+w/2}" y="${cy+24}" text-anchor="middle"
      font-family="-apple-system,sans-serif" font-size="3.5" font-weight="700"
      fill="#92400E">ID SCAN</text>`;
  }

  out += `</g>`;
  return out;
}

/* ═══════════════════════════════════════════════════════════════════════
   STATE MAPPER — option JSON keys → SVG state shape
   ═══════════════════════════════════════════════════════════════════════ */
function mapStateToSvg(state){
  const payments = Array.isArray(state.payments) ? state.payments : [];
  const upgrades = Array.isArray(state.upgrades) ? state.upgrades : [];
  const connectivity = Array.isArray(state.connectivity) ? state.connectivity : [];
  const screenMap  = {standard:'standard', medium_touch:'medium_touch', large_touch:'large_touch'};
  const topboxMap  = {none:'none', top_box:'top_box', led_top:'led_top', advertising:'advertising'};
  const wrapMap    = {standard:'none', logo:'logo', full_wrap:'full'};
  const sizeVal    = SIZES[state.size] ? state.size : 'standard';
  const systemVal  = ['standard','smart','advertising'].includes(state.system) ? state.system : 'standard';

  return {
    size:         sizeVal,
    system:       systemVal,
    temp:         state.temperature || 'ambient',
    delivery:     state.trays || 'coil',
    topbox:       topboxMap[state.top_box] || 'none',
    env:          state.environment === 'outdoor' ? 'outdoor' : 'indoor',
    screen:       screenMap[state.user_interface] || 'standard',
    wrap:         wrapMap[state.custom_wrap] || 'none',
    wrapColor:    S.WRAP_COLORS.navy,
    payments:     payments.length ? payments : ['codes'],
    connectivity: connectivity,
    camera:       upgrades.includes('camera'),
    led:          upgrades.includes('led_lights'),
    ageverify:    upgrades.includes('age_verify')
  };
}

/* ─── Register with the builder core ──────────────────────────────────── */
window.VMFS.registerRenderer('snack-beverage', {
  build:    buildMachineSVG,
  mapState: mapStateToSvg
});

})();
